import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

// ----------- Type Definitions -----------
export interface EmailTemplate {
  id?: string;
  type: string;
  name: string;
  subject: string;
  html: string;
  created_at?: string;
  updated_at?: string;
  dealer_id?: string;
}

export interface CreateEmailTemplateRequest {
  type: string;
  name: string;
  subject: string;
  html: string;
}

export interface UpdateEmailTemplateRequest {
  type?: string;
  name?: string;
  subject?: string;
  html?: string;
}

export interface EmailTemplateResponse {
  data: EmailTemplate[];
  total: number;
  page: number;
  limit: number;
}

class EmailTemplateApiService {
  static ENDPOINT = 'api/v2/templates';

  static async getEmailTemplates(dealerId: string, params?: {
    skip?: number;
    limit?: number;
    type?: string;
  }): Promise<APIResponseType<EmailTemplateResponse>> {
    try {
      const cleanParams = {
        ...params,
        dealer: dealerId,
      };

      const response = await apiService.get<APIResponseType<EmailTemplateResponse>>(
        this.ENDPOINT,
        cleanParams
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getEmailTemplateById(dealerId: string, templateId: string): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.get<APIResponseType<EmailTemplate>>(
        `${this.ENDPOINT}/${templateId}`,
        { dealer: dealerId }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async createEmailTemplate(dealerId: string, data: CreateEmailTemplateRequest): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.post<APIResponseType<EmailTemplate>>(
        this.ENDPOINT,
        data,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async updateEmailTemplate(dealerId: string, templateId: string, data: UpdateEmailTemplateRequest): Promise<APIResponseType<EmailTemplate>> {
    try {
      const response = await apiService.put<APIResponseType<EmailTemplate>>(
        `${this.ENDPOINT}/${templateId}`,
        data,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteEmailTemplate(dealerId: string, templateId: string): Promise<APIResponseType<any>> {
    try {
      const response = await apiService.delete<APIResponseType<any>>(
        `${this.ENDPOINT}/${templateId}`,
        { params: { dealer: dealerId } }
      );
      await apiService.invalidateCacheByPrefix(this.ENDPOINT, 'GET');
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async downloadEmailTemplate(dealerId: string, templateId: string): Promise<APIResponseType<Blob>> {
    try {
      const response = await apiService.get<Blob>(
        `${this.ENDPOINT}/download/${templateId}`,
        { 
          dealer: dealerId,
          responseType: 'blob'
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default EmailTemplateApiService;
